// Cómo se llama en Business Central cada cosa, según el país.
//
// El terminal decidía esto con comparaciones sueltas repartidas por la capa de
// sincronización: 29 sitios, 40 comparaciones. Y no son 29 decisiones distintas
// --son unos pocos ejes escritos muchas veces--. Cinco de esas reglas estaban
// **duplicadas en dos archivos**, con un comentario admitiéndolo («mismo
// criterio que buildFilters»). Una regla escrita dos veces se corrige una vez.
//
// Acá viven sólo los ejes que son **un dato**: el nombre de una entidad y el
// protocolo por el que se lee. Lo que es una regla con orden de ejecución --qué
// campos viajan en la cabecera, la ubicación de Costa Rica, los campos de la
// DGI de Panamá-- se queda donde está: sus comentarios documentan fallos ya
// medidos, y moverlos a una tabla perdería justo eso.
//
// **Los nueve países están escritos, incluidos Bolivia y Guatemala.** Antes
// caían al `else` de un ternario sin que nadie lo hubiera decidido, y si su
// ambiente publicaba otra cosa el fallo salía como «no se pudo sincronizar»,
// sin decir que la causa era el nombre. Escribirlos no los soporta, pero deja
// ver qué se asumió de ellos.
import { COUNTRY_CODES } from "../constants/countries";
import { anotarAvisoDeProceso } from "../services/syncLog";

/** OData es el camino de siempre; la API v2 sólo donde OData no funciona. */
const odata = (endpoint) => ({ endpoint, apiV2: false });
const apiV2 = (endpoint) => ({ endpoint, apiV2: true });

/**
 * Las entidades de Business Central, por país.
 *
 * `facturasRegistradas` y `abonosRegistrados` van **por separado a propósito**.
 * En Honduras y El Salvador la factura registrada se lee por la API v2 y el
 * abono registrado sigue por OData; hoy eso vive en un `isApi = !isCreditMemo`
 * que es fácil de perder de vista. Con una sola bandera «este país usa API v2»
 * las notas de crédito se romperían.
 */
const ENTIDADES = {
  [COUNTRY_CODES.ECUADOR]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  [COUNTRY_CODES.PARAGUAY]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  [COUNTRY_CODES.HONDURAS]: {
    cajas: odata("CajaTPV"),
    facturasRegistradas: apiV2("postedSalesInvoicesSant"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: apiV2("companyInformation"),
  },
  [COUNTRY_CODES.PERU]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  [COUNTRY_CODES.BOLIVIA]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  [COUNTRY_CODES.GUATEMALA]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  [COUNTRY_CODES.ELSALVADOR]: {
    cajas: odata("Cajas"),
    facturasRegistradas: apiV2("postedSalesInvoicesSant"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: apiV2("companyInformation"),
  },
  [COUNTRY_CODES.COSTARICA]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  [COUNTRY_CODES.PANAMA]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
  // Puerto Rico, comprobado contra su `$metadata` el 16/10/2026: publica las
  // cuatro por OData --`Cajas`, `PostedSalesInvoice`, `PostedSalesCreditMemo` y
  // `Empresa`--, y NO publica ni `CajaTPV` ni `postedSalesInvoicesSant` ni
  // `companyInformation`, que son las formas de Honduras y El Salvador.
  [COUNTRY_CODES.PUERTORICO]: {
    cajas: odata("Cajas"),
    facturasRegistradas: odata("PostedSalesInvoice"),
    abonosRegistrados: odata("PostedSalesCreditMemo"),
    empresa: odata("Empresa"),
  },
};

/**
 * Lo que se usa cuando el país no está en la tabla.
 *
 * No es «la configuración de Ecuador», como en `configuracionDePais`: es el
 * camino neutro, que es lo que corresponde cuando lo que falta es el nombre de
 * un servicio web y no el dibujo de una pantalla. Mismo criterio que el
 * respaldo de la configuración de impresión de tickets.
 */
const NEUTRO = ENTIDADES[COUNTRY_CODES.ECUADOR];

/**
 * Cómo se llama el campo de la caja en el filtro de los documentos registrados.
 *
 * Sólo cambia en los **registrados**: en los borradores se llama `Caja` en los
 * nueve países. Perú es el único que lo publica con ese mismo nombre también
 * registrado.
 */
const CAJA_EN_REGISTRADAS = { [COUNTRY_CODES.PERU]: "Caja" };
const CAJA_EN_REGISTRADAS_POR_DEFECTO = "TPV";

const yaAvisados = new Set();

const avisarUnaVez = (pais, que) => {
  const clave = `${que}·${pais}`;
  if (yaAvisados.has(clave)) return;
  yaAvisados.add(clave);
  anotarAvisoDeProceso(
    `Integración con Business Central: no hay ${que} para «${pais || "(sin país)"}», se usa el camino neutro`
  );
};

/**
 * La entidad de Business Central que le toca a un país.
 *
 * @param {string} pais     Código del país activo.
 * @param {string} entidad  `"cajas"`, `"facturasRegistradas"` o `"abonosRegistrados"`.
 * @returns {{endpoint: string, apiV2: boolean}}
 */
export const entidadDelPais = (pais, entidad) => {
  const delPais = ENTIDADES[pais];
  if (!delPais) avisarUnaVez(pais, "entidades");
  return (delPais || NEUTRO)[entidad];
};

/**
 * El nombre del campo de la caja en el filtro.
 *
 * @param {string} pais
 * @param {boolean} registrado  Si el documento es uno registrado.
 * @returns {string}
 */
export const campoDeLaCaja = (pais, registrado) =>
  registrado ? CAJA_EN_REGISTRADAS[pais] || CAJA_EN_REGISTRADAS_POR_DEFECTO : "Caja";

/**
 * Cómo se comporta cada país en el registro y en lo fiscal.
 *
 * Son las banderas que estaban escritas como comparaciones sueltas repartidas
 * por la sincronización. Acá sólo vive **el dato**; el cuerpo de cada rama se
 * queda donde está, porque son cien líneas con guardias y recuperación que una
 * tabla no puede representar.
 */
const COMPORTAMIENTO = {
  [COUNTRY_CODES.ECUADOR]: {},
  [COUNTRY_CODES.PARAGUAY]: { registraDesdeElTerminal: true },
  [COUNTRY_CODES.HONDURAS]: { registraDesdeElTerminal: true, cajaConCamposFiscales: true },
  // Perú sí contabiliza desde el terminal. Su entrada estaba vacía --o sea,
  // `registraDesdeElTerminal: false` por respaldo-- mientras la cadena de `||`
  // de `invoiceSyncService` lo registraba igual, porque esa cadena era la que
  // mandaba y esta bandera no la leía nadie. Desde el 17/10/2026 la bandera es la
  // única fuente, así que aquí queda escrito lo que el terminal ya hacía.
  [COUNTRY_CODES.PERU]: { registraDesdeElTerminal: true },
  [COUNTRY_CODES.BOLIVIA]: {},
  [COUNTRY_CODES.GUATEMALA]: {},
  [COUNTRY_CODES.ELSALVADOR]: {},
  [COUNTRY_CODES.COSTARICA]: {
    registraDesdeElTerminal: true,
    tieneTimbrado: true,
    // `postAndSend` registra y después manda el documento por el correo nativo
    // de Business Central. En Costa Rica ese envío no hace falta --el
    // comprobante lo manda CertificaE-- y donde no hay cuenta de correo falla
    // con 400 **con el abono ya registrado y timbrado**.
    accionDelAbono: "Microsoft.NAV.post",
  },
  [COUNTRY_CODES.PANAMA]: {
    registraDesdeElTerminal: true,
    // Panamá comparte ambiente y extensión con Costa Rica, así que su empresa
    // tiene igualmente el campo de la migración costarricense y viene relleno.
    // Importarlo sería inventarle al documento un tipo que su país no tiene.
    importaTipoDeComprobante: false,
  },
  // Puerto Rico: registra desde el terminal, como Costa Rica y Panamá.
  //
  // Estuvo apagado del 16 al 18/10/2026 por un `Permissions` que le faltaba al
  // codeunit 62103 de su migración. El historial está en
  // `Invoice/extensions/costarica/cierreFiscal.js`.
  //
  // `tieneTimbrado` se queda en false por defecto, y aquí es definitivo: no hay
  // facturación electrónica ninguna que timbrar.
  //
  // `importaTipoDeComprobante` en false por lo mismo que Panamá, y medido igual:
  // el 16/10/2026, las 3.000 facturas registradas más recientes de Santillana
  // Puerto Rico traen `EXCCRIElectronicDocumentType` = «Factura» --3.000 de
  // 3.000--, que es el valor por defecto que arrastra por compartir el ambiente
  // de Costa Rica y que allá no significa nada. Importarlo sería inventarle al
  // documento un tipo de comprobante que su país no tiene.
  [COUNTRY_CODES.PUERTORICO]: {
    registraDesdeElTerminal: true,
    importaTipoDeComprobante: false,
    // El abono se registra sin mandarlo por correo, por lo mismo que en Costa
    // Rica y con más razón: allá el comprobante lo manda CertificaE, y aquí no
    // hay ningún comprobante electrónico que mandar. `postAndSend` sólo añade un
    // envío que no aporta nada y que puede fallar con 400 **con el abono ya
    // registrado**, dejando al terminal creyendo que falló algo que sí está
    // hecho.
    //
    // Medido el 16/10/2026: 646 de los 1.275 clientes de Santillana Puerto Rico
    // no tienen `E_Mail`. No es prueba de cuál de las dos cosas falta --la
    // dirección del cliente o la cuenta de salida de Business Central--, pero
    // con la mitad del catálogo sin correo no hay motivo para arriesgar el paso.
    accionDelAbono: "Microsoft.NAV.post",
  },
};

const POR_DEFECTO = {
  registraDesdeElTerminal: false,
  tieneTimbrado: false,
  cajaConCamposFiscales: false,
  importaTipoDeComprobante: true,
  accionDelAbono: "Microsoft.NAV.postAndSend",
};

/**
 * Una bandera de comportamiento del país.
 *
 * @param {string} pais
 * @param {keyof POR_DEFECTO} que
 */
export const comportamientoDelPais = (pais, que) => {
  const delPais = COMPORTAMIENTO[pais];
  if (!delPais) avisarUnaVez(pais, "comportamiento");
  const propio = (delPais || {})[que];
  return propio === undefined ? POR_DEFECTO[que] : propio;
};

/** Los campos fiscales que sólo trae la caja de Honduras. */
export const CAMPOS_FISCALES_DE_LA_CAJA = [
  "Tienda",
  "Numerador_facturas",
  "Numerador_abonos",
  "Numerador_facturas_registradas",
  "Numerador_abonos_registrados",
  "Numerador_facturas_NFC",
  "Numerador_abonos_NFC",
];

/** Los países declarados, para poder comprobar que no falta ninguno. */
export const PAISES_DECLARADOS = Object.keys(ENTIDADES);

/** Sólo para las pruebas: vuelve a avisar de lo ya avisado. */
export const olvidarAvisosDeIntegracion = () => yaAvisados.clear();
